import { reportErrorToConsole } from '@src/services/error-logger'
import { apiClient } from '@src/services/http-common'
import { FailedTransactionItem } from '@src/types/transaction-retry'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const SCOPE = 'transaction-retry'

// QueryKey factory for consistent query key generation
export const transactionRetryQueryKeys = {
  all: [SCOPE] as const,
  failedTransactions: (companyId?: string) => [...transactionRetryQueryKeys.all, 'failed', companyId] as const,
}

// Types for the new API response format
interface ApiFileItem {
  fileName: string
  companyId: string
  createdAt: string
  fileSize?: number
  size?: number // Alternative property name
  errorCount: number
}

interface ApiResponse {
  success?: boolean
  files?: ApiFileItem[]
  message?: string
}

const getFailedTransactions = async (companyId?: string): Promise<FailedTransactionItem[]> => {
  const params: Record<string, string> = {}

  if (companyId) {
    params.companyId = companyId
  }

  try {
    const response = await apiClient.get<ApiResponse>('accounting/failed-transactions', {
      params,
    })

    // Handle different possible response structures
    let dataArray: ApiFileItem[] = []

    if (response.data && Array.isArray(response.data.files)) {
      dataArray = response.data.files
    } else {
      dataArray = []
    }

    // Transform API response to match UI interface
    return dataArray
      .filter((item) => item && typeof item === 'object') // Filter out invalid items
      .map((item): FailedTransactionItem => {
        // Safely handle potentially undefined properties
        const fileName = item.fileName || 'unknown'
        const createdAt = item.createdAt || new Date().toISOString()
        const errorCount = item.errorCount ?? 0

        return {
          id: `${fileName}-${createdAt}`,
          fileName,
          date: new Date(createdAt),
          errorCount,
        }
      })
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

export const useGetFailedTransactions = (companyId?: string) => {
  const { data, isLoading, error } = useQuery({
    queryKey: transactionRetryQueryKeys.failedTransactions(companyId),
    queryFn: () => getFailedTransactions(companyId),
  })

  return [data, isLoading, error] as const
}

const retryTransactions = async (transactionIds: string[]): Promise<void> => {
  await apiClient.post('RetryFailedTransactions', { transactionIds })
}

const retrySingleTransaction = async (fileName: string, companyId?: string): Promise<void> => {
  const payload: { fileName: string; companyId?: string } = { fileName }
  if (companyId) {
    payload.companyId = companyId
  }
  await apiClient.post('accounting/failed-transactions/retry', payload)
}

// NEW: Direct download function that replaces SAS token usage
const downloadReport = async (fileName: string, companyId?: string): Promise<void> => {
  const payload: { fileName: string; companyId?: string } = { fileName }
  if (companyId) {
    payload.companyId = companyId
  }

  try {
    const response = await apiClient.post('accounting/failed-transactions/download', payload, {
      responseType: 'blob', // Important: tell axios this is a binary response
    })

    // Create blob and download
    const blob = new Blob([response.data])
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

interface RetryMultipleResult {
  succeeded: string[]
  failed: { fileName: string; error: unknown }[]
}

const retryMultipleTransactions = async (fileNames: string[], companyId?: string): Promise<RetryMultipleResult> => {
  const succeeded: string[] = []
  const failed: { fileName: string; error: unknown }[] = []

  // Boucler pour lancer chaque fichier une apr�s l'autre
  for (const fileName of fileNames) {
    try {
      await retrySingleTransaction(fileName, companyId)
      succeeded.push(fileName)
    } catch (error) {
      reportErrorToConsole(error)
      failed.push({ fileName, error })
      // Continue avec les autres fichiers m�me si un �choue
    }
  }

  // Si au moins un fichier a �chou�, on l�ve une erreur avec les d�tails
  if (failed.length > 0) {
    class RetryError extends Error {
      details: { succeeded: string[]; failed: { fileName: string; error: unknown }[] }
      constructor(message: string, details: { succeeded: string[]; failed: { fileName: string; error: unknown }[] }) {
        super(message)
        this.details = details
        Object.setPrototypeOf(this, RetryError.prototype)
      }
    }
    throw new RetryError(`Failed to retry ${failed.length} out of ${fileNames.length} transactions`, {
      succeeded,
      failed,
    })
  }

  return { succeeded, failed }
}

export const useRetryTransactions = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: retryTransactions,
    onSuccess: () => {
      // Invalidate and refetch failed transactions queries
      void queryClient.invalidateQueries({ queryKey: transactionRetryQueryKeys.all })
    },
  })
}

export const useRetrySingleTransaction = (companyId?: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (fileName: string) => retrySingleTransaction(fileName, companyId),
    onSuccess: () => {
      // Invalidate and refetch failed transactions queries
      void queryClient.invalidateQueries({ queryKey: transactionRetryQueryKeys.all })
    },
  })
}

export const useRetryMultipleTransactions = (companyId?: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (fileNames: string[]) => retryMultipleTransactions(fileNames, companyId),
    onSuccess: () => {
      // Invalidate and refetch failed transactions queries
      void queryClient.invalidateQueries({ queryKey: transactionRetryQueryKeys.all })
    },
    onError: (error) => {
      reportErrorToConsole(error)
    },
  })
}

// NEW: Hook for direct download that replaces useGetSasToken
export const useDownloadReport = (companyId?: string) => {
  return useMutation({
    mutationFn: (fileName: string) => downloadReport(fileName, companyId),
  })
}